import { useCallback, useEffect, useState } from 'react'
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'

import { HOTSPOT_DEMO_COURSE } from '../dev/hotspotDemoCourse.js'
import { login } from '../legacy/api.js'
import { CoursePlayer } from './CoursePlayer.js'
import {
  ErrorScreen,
  LaunchScreen,
  LoadingScreen,
  LoginScreen,
  type Language,
} from './Screens.js'
import { COURSE_ROUTES, DEMOS, DEMO_SUCCESS, DEMO_USER_ID, type CourseRoute } from './routes.js'
import styles from './Screens.module.css'
import logo from '../assets/logo.png'

/** Stav předávaný na /player navigací — přežije i refresh (history.state). */
interface PlayerNavState {
  userId: string
  lecture: string
  language: Language
  /** Jen u dem: kam přejít po dokončení místo dokončovacího endpointu. */
  successUrl?: string | undefined
}

function isPlayerNavState(value: unknown): value is PlayerNavState {
  if (!value || typeof value !== 'object') return false
  const v = value as Partial<PlayerNavState>
  return (
    typeof v.userId === 'string' &&
    typeof v.lecture === 'string' &&
    (v.successUrl === undefined || typeof v.successUrl === 'string')
  )
}

/* ------------------------------------------------------------------ *
 * / — úvod s testem zvuku
 * ------------------------------------------------------------------ */

function StartScreen(): React.ReactElement {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const username = params.get('username') ?? ''
  const [playing, setPlaying] = useState(false)
  const [audio] = useState(() => new Audio(new URL('../assets/AUDIO_TEST.mp3', import.meta.url).href))

  useEffect(() => {
    const stop = (): void => setPlaying(false)
    audio.addEventListener('ended', stop)
    return () => {
      audio.removeEventListener('ended', stop)
      audio.pause()
    }
  }, [audio])

  const toggle = useCallback(() => {
    if (playing) {
      audio.pause()
      setPlaying(false)
    } else {
      void audio.play()
      setPlaying(true)
    }
  }, [audio, playing])

  return (
    <main className={styles.page}>
      <div className={styles.card}>
        <img className={styles.logo} src={logo} alt="SafeTutor" />
        <h1 className={styles.title}>Test zvuku</h1>
        <p className={styles.text}>
          K tomuto kurzu je nutné zapnout zvuk. Ujistěte se, že máte zvuk zapnutý a reproduktory
          nebo sluchátka připojená.
        </p>
        <button type="button" className={styles.button} onClick={toggle}>
          {playing ? '⏸ ZASTAVIT UKÁZKU' : '▶ PŘEHRÁT UKÁZKU'}
        </button>
        <p className={styles.text}>
          Vyzkoušejte prosím, zda bez problémů rozumíte zvukové stopě, a nastavte si hlasitost.
        </p>
        <button
          type="button"
          className={styles.button}
          onClick={() =>
            navigate(username === '' ? '/login' : `/login?username=${encodeURIComponent(username)}`)
          }
        >
          SPUSTIT
        </button>
      </div>
    </main>
  )
}

/* ------------------------------------------------------------------ *
 * /login — přihlášení jménem
 * ------------------------------------------------------------------ */

function LoginRoute(): React.ReactElement {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const initial = params.get('username')

  const [username, setUsername] = useState(
    initial && initial !== 'undefined' ? initial : '',
  )
  const [failed, setFailed] = useState(false)
  const [busy, setBusy] = useState(false)

  const submit = useCallback(() => {
    setBusy(true)
    setFailed(false)

    void login(username.trim()).then((result) => {
      setBusy(false)
      if (!result.ok) {
        setFailed(true)
        return
      }
      const state: PlayerNavState = {
        userId: result.userId,
        lecture: result.lecture,
        language: 'cze',
      }
      navigate('/player', { state })
    })
  }, [username, navigate])

  return (
    <LoginScreen
      username={username}
      onChange={setUsername}
      onSubmit={submit}
      failed={failed}
      busy={busy}
    />
  )
}

/* ------------------------------------------------------------------ *
 * /player — samotný kurz
 * ------------------------------------------------------------------ */

function PlayerRoute(): React.ReactElement {
  const { state } = useLocation()

  // Přímý vstup na /player bez kontextu — vrátíme uživatele na přihlášení
  // místo bílé obrazovky, kterou v takové situaci dávala původní aplikace.
  if (!isPlayerNavState(state)) return <Navigate to="/login" replace />

  // Demo se nezapisuje — po dokončení jde na svou successUrl, jako ve staré
  // aplikaci. Záloha pro stav z historie, který successUrl ještě nenesl:
  // s uživatelem '0' se na dokončovací endpoint nesmí nikdy.
  const successUrl =
    state.successUrl ?? (state.userId === DEMO_USER_ID ? DEMO_SUCCESS : undefined)

  return (
    <CoursePlayer
      lecture={state.lecture}
      userId={state.userId}
      language={state.language ?? 'cze'}
      successUrl={successUrl}
      // Navigace celého okna, ne fetch — server na dokončovací adrese nastaví
      // přihlášení a přesměruje do profilu. JSON vrací jen při chybě.
      onFinish={(url) => {
        window.location.href = url
      }}
      onQuit={() => {
        window.location.href = '/'
      }}
    />
  )
}

/* ------------------------------------------------------------------ *
 * Kurzy spouštěné z odkazu
 * ------------------------------------------------------------------ */

function CourseEntry({ route }: { route: CourseRoute }): React.ReactElement {
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const start = useCallback(() => {
    if (route.kind === 'demo') {
      const state: PlayerNavState = {
        userId: DEMO_USER_ID,
        lecture: route.lecture,
        language: route.language,
        successUrl: route.successUrl,
      }
      navigate('/player', { state })
      return
    }

    setBusy(true)
    setError(null)
    void login(route.username).then((result) => {
      setBusy(false)
      if (!result.ok) {
        setError('Kurz se nepodařilo spustit. Zkuste to prosím znovu.')
        return
      }
      const state: PlayerNavState = {
        userId: result.userId,
        lecture: result.lecture,
        language: route.language,
      }
      navigate('/player', { state })
    })
  }, [route, navigate])

  if (busy) return <LoadingScreen label="Spouštím kurz…" />
  if (error !== null) {
    return <ErrorScreen title="Kurz se nepodařilo spustit" detail={error} onRetry={start} />
  }

  return <LaunchScreen title={route.title} language={route.language} onStart={start} />
}

/* ------------------------------------------------------------------ *
 * /vice — rozcestník dem
 * ------------------------------------------------------------------ */

function DemoPage(): React.ReactElement {
  const navigate = useNavigate()
  return (
    <main className={styles.page}>
      <div className={styles.card}>
        <img className={styles.logo} src={logo} alt="SafeTutor" />
        <h1 className={styles.title}>Ukázkové kurzy</h1>
        {DEMOS.map((demo) => (
          <button
            key={demo.path}
            type="button"
            className={styles.button}
            onClick={() => navigate(demo.path)}
          >
            {demo.title}
          </button>
        ))}
      </div>
    </main>
  )
}

/* ------------------------------------------------------------------ *
 * /test-hotspoty — vývojářská pomůcka, jen klikací otázky
 * ------------------------------------------------------------------ */

function HotspotTestRoute(): React.ReactElement {
  return (
    <CoursePlayer
      lecture="test-hotspoty"
      userId="0"
      language="cze"
      course={HOTSPOT_DEMO_COURSE}
      // Pomůcka zůstává, jak byla: na verdiktu čeká na kliknutí a pak se
      // načte znovu. Nic nezapisuje, takže nemá kam spěchat.
      autoFinish={false}
      onFinish={() => {
        window.location.href = '/test-hotspoty'
      }}
      onQuit={() => {
        window.location.href = '/'
      }}
    />
  )
}

/* ------------------------------------------------------------------ */

export function App(): React.ReactElement {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<StartScreen />} />
        <Route path="/login" element={<LoginRoute />} />
        <Route path="/player" element={<PlayerRoute />} />
        <Route path="/vice" element={<DemoPage />} />
        <Route path="/test-hotspoty" element={<HotspotTestRoute />} />

        {COURSE_ROUTES.map((route) => (
          <Route key={route.path} path={route.path} element={<CourseEntry route={route} />} />
        ))}

        <Route
          path="*"
          element={
            <ErrorScreen
              title="Stránka nenalezena"
              detail="Požadovaná stránka neexistuje."
              onSkip={() => {
                window.location.href = '/'
              }}
              skipLabel="Na úvod"
            />
          }
        />
      </Routes>
    </BrowserRouter>
  )
}
